import { useState } from 'react';
import { getBackendDetails } from '@/utils/getBackendDetails';
import { User } from '@/generated/graphql';
import { useJWT } from './useAuth';

interface SignupResponse {
  success: boolean;
  user: User;
  accessToken: string;
}

export function useSignup() {
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const { storeAccessToken } = useJWT();

  const signup = async (email: string, password: string) => {
    setIsLoading(true);
    setError(null);

    const { hostname, httpProtocol } = getBackendDetails();
    const signupUrl = `${httpProtocol}://${hostname}/auth/signup`;

    try {
      const response = await fetch(signupUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email,
          password,
        }),
        credentials: 'include', // Important for cookies
      });

      const data: SignupResponse = await response.json();

      if (!response.ok) {
        throw new Error(data.user ? 'User already exists' : 'Signup failed');
      }

      // Store the access token
      if (data.success && data.accessToken) {
        storeAccessToken(data.accessToken);
      }

      return data;
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'An error occurred during signup',
      );
      return null;
    } finally {
      setIsLoading(false);
    }
  };

  return {
    signup,
    error,
    isLoading,
  };
}
